import * as THREE from 'three';
import { SNUFFER } from './gameplay.js';

const PALETTES = {
  player: { body: 0x1d4a59, accent: 0xe8bb69, skin: 0xd4a982, hair: 0x171928, weapon: 0xd8e0dd },
  mara: { body: 0x3c466d, accent: 0x91d4bc, skin: 0x8d5b4c, hair: 0x1c1b2a, weapon: 0xa7d9d2 },
  vampire: { body: 0x321c35, accent: 0xef6b72, skin: 0x8d3f52, hair: 0x14101f, weapon: 0xe0d5d4 },
  werewolf: { body: 0x5a3b31, accent: 0x9fbf7a, skin: 0x916247, hair: 0x312522, weapon: 0xc4b69a },
  boss: { body: 0x4e172c, accent: 0xf0a05e, skin: 0x9a4960, hair: 0x100d1a, weapon: 0xf0c36c },
  marshal: { body: 0x34233e, accent: 0xd6a15e, skin: 0x9b6c5f, hair: 0x171322, weapon: 0xe4c47a },
  thrall: { body: 0x2f2740, accent: 0xc9a2ff, skin: 0x8f86a8, hair: 0x16121f, weapon: 0xcfc6e2 },
};

function mat(color, roughness = 0.88) {
  return new THREE.MeshStandardMaterial({ color, roughness, flatShading: true });
}

function basic(color, opacity = 1) {
  return new THREE.MeshBasicMaterial({ color, transparent: opacity < 1, opacity, blending: opacity < 1 ? THREE.AdditiveBlending : THREE.NormalBlending });
}

// Fine detail contributes nothing readable to a 1024px shadow map over the whole play area, so it
// is excluded from the shadow pass instead of paying a draw call per eye, crest, and hair cone.
function markFineDetail(object) {
  if (object) object.userData.noShadow = true;
  return object;
}

function attachShadow(character) {
  const pixels = new Uint8Array(32 * 32 * 4);
  for (let y = 0; y < 32; y += 1) {
    for (let x = 0; x < 32; x += 1) {
      const index = (y * 32 + x) * 4;
      const radius = Math.hypot((x - 15.5) / 15.5, (y - 15.5) / 15.5);
      pixels[index] = pixels[index + 1] = pixels[index + 2] = 255;
      pixels[index + 3] = Math.round(255 * Math.max(0, 1 - radius) ** 1.6);
    }
  }
  const texture = new THREE.DataTexture(pixels, 32, 32);
  texture.magFilter = texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(1.6, 1.3), new THREE.MeshBasicMaterial({ map: texture, color: 0x02030b, transparent: true, opacity: 0.65, depthWrite: false }));
  markFineDetail(shadow);
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = 0.035;
  character.add(shadow);
  character.userData.shadow = shadow;
}

function makeLimb(parent, length, radius, color, x, y, z) {
  const pivot = new THREE.Group();
  pivot.position.set(x, y, z);
  const limb = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.84, radius, length, 6), mat(color));
  limb.position.y = -length / 2;
  pivot.add(limb);
  parent.add(pivot);
  return pivot;
}

function makeHead(character, palette, kind) {
  const head = new THREE.Group();
  head.position.y = 2.2;
  const skull = new THREE.Mesh(new THREE.DodecahedronGeometry(kind === 'boss' ? 0.47 : 0.36, 0), mat(palette.skin));
  head.add(skull);
  const hair = markFineDetail(new THREE.Mesh(new THREE.ConeGeometry(kind === 'werewolf' ? 0.34 : 0.28, kind === 'werewolf' ? 0.42 : 0.3, 5), mat(palette.hair)));
  hair.position.y = 0.3;
  hair.rotation.z = Math.PI;
  head.add(hair);
  const eyeMaterial = basic(kind === 'werewolf' ? 0xd8eaa0 : kind === 'boss' ? 0xf0a05e : kind === 'marshal' ? 0xf0c36c : kind === 'thrall' ? 0xc9a2ff : 0xef6b72);
  for (const x of [-0.14, 0.14]) {
    const eye = markFineDetail(new THREE.Mesh(new THREE.SphereGeometry(0.055, 8, 6), eyeMaterial));
    eye.position.set(x, 0.06, 0.31);
    head.add(eye);
  }
  if (kind === 'vampire' || kind === 'boss') {
    const collar = markFineDetail(new THREE.Mesh(new THREE.ConeGeometry(0.48, 0.28, 6, 1, true), mat(palette.accent)));
    collar.position.set(0, -0.34, 0.02);
    collar.rotation.x = Math.PI;
    head.add(collar);
  }
  if (kind === 'werewolf') {
    for (const x of [-0.23, 0.23]) {
      const ear = markFineDetail(new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.32, 4), mat(palette.body)));
      ear.position.set(x, 0.37, 0);
      ear.rotation.z = x < 0 ? -0.25 : 0.25;
      head.add(ear);
    }
  }
  if (kind === 'marshal') {
    for (const x of [-0.25, 0.25]) {
      const antler = markFineDetail(new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.58, 5), mat(palette.accent, 0.56)));
      antler.position.set(x, 0.38, 0);
      antler.rotation.z = x < 0 ? -0.5 : 0.5;
      head.add(antler);
    }
  }
  if (kind === 'thrall') {
    // A census figure is all hood: the head sits inside it rather than wearing a hat over it, so a
    // wick-servant is readable as a silhouette at the same distance a Court Hound is.
    const hood = markFineDetail(new THREE.Mesh(new THREE.ConeGeometry(0.44, 0.62, 6, 1, true), mat(palette.hair)));
    hood.position.y = 0.24;
    hood.rotation.z = Math.PI;
    head.add(hood);
    const collar = markFineDetail(new THREE.Mesh(new THREE.ConeGeometry(0.5, 0.3, 6, 1, true), mat(palette.body, 0.9)));
    collar.position.set(0, -0.34, 0.02);
    collar.rotation.x = Math.PI;
    head.add(collar);
  }
  character.add(head);
  return head;
}

function makeSilhouette(body, head, palette, kind) {
  const accentMaterial = mat(palette.accent, 0.58);
  const mantle = new THREE.Mesh(new THREE.ConeGeometry(kind === 'boss' ? 0.78 : kind === 'vampire' || kind === 'marshal' ? 0.61 : 0.55, kind === 'boss' ? 0.58 : 0.42, kind === 'boss' ? 8 : 6, 1, true), accentMaterial);
  mantle.position.y = kind === 'boss' ? 0.92 : 0.82;
  mantle.scale.z = 0.82;
  body.add(mantle);
  const crest = markFineDetail(new THREE.Mesh(new THREE.OctahedronGeometry(kind === 'boss' ? 0.17 : 0.13, 0), accentMaterial));
  crest.position.set(0, 0.58, 0.43);
  body.add(crest);
  if (kind === 'werewolf') {
    const snout = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.4, 4), mat(palette.skin, 0.96));
    snout.position.set(0, -0.04, 0.39);
    snout.rotation.x = Math.PI / 2;
    head.add(snout);
  }
  if (kind === 'boss') {
    const crown = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.38, 5), accentMaterial);
    crown.position.y = 0.55;
    head.add(crown);
  }
  if (kind === 'marshal') {
    const gorget = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.07, 5, 12), accentMaterial);
    gorget.rotation.x = Math.PI / 2;
    gorget.position.y = 0.55;
    body.add(gorget);
    const packMark = new THREE.Mesh(new THREE.OctahedronGeometry(0.18, 0), accentMaterial);
    packMark.position.set(0, 1.05, 0.42);
    body.add(packMark);
    return { mantle, crest, gorget, packMark };
  }
  return { mantle, crest };
}

function makeAttackTelegraph(character) {
  const group = new THREE.Group();
  const telegraphMaterial = basic(0xe8bb69, 0.72);
  telegraphMaterial.depthWrite = false;
  const ground = markFineDetail(new THREE.Mesh(new THREE.RingGeometry(0.62, 0.78, 20, 1, -0.85, 1.7), telegraphMaterial));
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0.07;
  group.add(ground);
  const blade = markFineDetail(new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.035, 4, 14, Math.PI * 1.35), telegraphMaterial));
  blade.position.set(0.16, 1.28, 0.55);
  blade.rotation.z = -0.7;
  group.add(blade);
  group.visible = false;
  group.userData = { ground, blade, material: telegraphMaterial };
  character.add(group);
  return group;
}
function makeMarshalLink(character, palette) {
  const group = new THREE.Group();
  const outerMaterial = basic(palette.accent, 0.5);
  const innerMaterial = basic(0x9be5d1, 0.76);
  const outer = markFineDetail(new THREE.Mesh(new THREE.TorusGeometry(0.85, 0.035, 5, 24), outerMaterial));
  const inner = markFineDetail(new THREE.Mesh(new THREE.TorusGeometry(0.58, 0.025, 5, 20), innerMaterial));
  const beacon = markFineDetail(new THREE.Mesh(new THREE.OctahedronGeometry(0.16, 0), innerMaterial));
  outer.rotation.x = Math.PI / 2;
  inner.rotation.x = Math.PI / 2;
  outer.position.y = 1.15;
  inner.position.y = 1.15;
  beacon.position.y = 1.16;
  group.add(outer, inner, beacon);
  character.add(group);
  return { group, outer, inner, beacon, outerMaterial, innerMaterial };
}

function makeWeapon(character, palette, kind) {
  const weapon = new THREE.Group();
  weapon.position.set(0.54, 1.4, 0.2);
  const handle = markFineDetail(new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, 0.5, 6), mat(0x1a1820)));
  handle.rotation.z = -0.3;
  weapon.add(handle);
  const blade = new THREE.Mesh(new THREE.ConeGeometry(kind === 'werewolf' ? 0.08 : 0.12, kind === 'boss' ? 1.2 : 0.88, 4), mat(palette.weapon, 0.36));
  blade.position.y = 0.62;
  blade.rotation.z = -0.3;
  weapon.add(blade);
  character.add(weapon);
  return weapon;
}

function makeCape(character, palette, kind) {
  if (kind === 'player' || kind === 'mara' || kind === 'vampire' || kind === 'boss' || kind === 'marshal' || kind === 'thrall') {
    const cape = new THREE.Mesh(new THREE.ConeGeometry(kind === 'boss' ? 0.78 : 0.55, kind === 'boss' ? 2.0 : 1.45, 5, 1, true), mat(palette.body, 0.98));
    cape.position.set(0, 1.25, -0.22);
    cape.scale.z = 0.48;
    cape.rotation.x = -0.1;
    character.add(cape);
    return cape;
  }
  return null;
}

// A wick-servant carries a hook, not a blade: a long shaft with a crook at the top and one cold
// ember under it. It is the only weapon in Vespera that is built for putting a light out.
function makeSnufferHook(character, palette) {
  const pole = new THREE.Group();
  pole.position.set(0.5, 1.28, 0.16);
  const shaft = markFineDetail(new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.042, 1.55, 6), mat(palette.weapon)));
  shaft.position.y = 0.5;
  const crook = markFineDetail(new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.036, 5, 12, Math.PI * 1.45), mat(palette.accent, 0.5)));
  crook.position.set(0, 1.3, 0.07);
  crook.rotation.set(Math.PI / 2, 0, 0.6);
  const ember = markFineDetail(new THREE.Mesh(new THREE.OctahedronGeometry(0.075, 0), basic(0xc9a2ff, 0.85)));
  ember.position.set(-0.06, 1.22, 0.12);
  pole.add(shaft, crook, ember);
  character.add(pole);
  return { group: pole, crook, ember };
}

// The channel ring is the whole read on a wick-servant: it only exists while the servant is
// putting a pylon out, and it tightens as the 2.6 s runs down. A player can read the remaining
// time off the ring without the HUD, and the HUD names it from the same number.
function makeSnuffRing(character) {
  const ring = markFineDetail(new THREE.Mesh(new THREE.RingGeometry(0.86, 1.22, 20, 1), basic(0xc25a6a, 0.42)));
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.07;
  ring.visible = false;
  character.add(ring);
  return ring;
}

export function createCharacter(kind = 'player', options = {}) {
  const palette = { ...PALETTES[kind], ...(options.palette || {}) };
  const character = new THREE.Group();
  character.name = kind;
  character.userData = {
    kind,
    hp: options.hp || (kind === 'boss' ? 460 : kind === 'player' ? 100 : 54),
    maxHp: options.hp || (kind === 'boss' ? 460 : kind === 'player' ? 100 : 54),
    speed: options.speed || (kind === 'player' ? 6.2 : kind === 'boss' ? 2.4 : 3.3),
    state: 'idle',
    attackTimer: 0,
    attackCooldown: 0,
    invulnerable: 0,
    hitFlash: 0,
    phase: 0,
    patrolSeed: options.seed || 1,
    home: options.position ? options.position.clone() : new THREE.Vector3(),
    target: null,
    alertness: 0,
    dead: false,
    movement: 0,
  };
  const scale = options.scale || (kind === 'boss' ? 1.7 : kind === 'player' ? 1 : 0.88);
  character.scale.setScalar(scale);
  if (options.position) character.position.copy(options.position);
  character.userData.baseScale = scale;

  const body = new THREE.Group();
  body.position.y = 0.92;
  const torso = new THREE.Mesh(new THREE.CylinderGeometry(kind === 'boss' ? 0.52 : 0.36, kind === 'boss' ? 0.66 : 0.46, kind === 'boss' ? 1.35 : 1.05, 6), mat(palette.body));
  torso.position.y = 0.45;
  body.add(torso);
  const belt = markFineDetail(new THREE.Mesh(new THREE.TorusGeometry(kind === 'boss' ? 0.58 : 0.4, 0.055, 5, 12), mat(palette.accent, 0.38)));
  belt.rotation.x = Math.PI / 2;
  belt.position.y = 0.1;
  body.add(belt);
  character.add(body);
  character.userData.body = body;

  const head = makeHead(character, palette, kind);
  const leftArm = makeLimb(character, kind === 'boss' ? 1.05 : 0.82, kind === 'boss' ? 0.16 : 0.12, palette.skin, -0.46, 1.6, 0);
  const rightArm = makeLimb(character, kind === 'boss' ? 1.05 : 0.82, kind === 'boss' ? 0.16 : 0.12, palette.skin, 0.46, 1.6, 0);
  const leftLeg = makeLimb(character, kind === 'boss' ? 1.2 : 0.9, kind === 'boss' ? 0.2 : 0.15, palette.body, -0.21, 0.82, 0);
  const rightLeg = makeLimb(character, kind === 'boss' ? 1.2 : 0.9, kind === 'boss' ? 0.2 : 0.15, palette.body, 0.21, 0.82, 0);
  const cape = makeCape(character, palette, kind);
  const weapon = kind === 'thrall' ? null : makeWeapon(character, palette, kind);
  const snuffer = kind === 'thrall' ? makeSnufferHook(character, palette) : null;
  const snuffRing = kind === 'thrall' ? makeSnuffRing(character) : null;
  const silhouette = makeSilhouette(body, head, palette, kind);
  const packLink = kind === 'marshal' ? makeMarshalLink(character, palette) : null;
  const telegraph = makeAttackTelegraph(character);
  const aura = markFineDetail(new THREE.Mesh(new THREE.SphereGeometry(kind === 'boss' ? 1.4 : 0.78, 12, 8), basic(kind === 'werewolf' ? 0x91bf7a : kind === 'boss' ? 0xef6b72 : kind === 'marshal' ? 0xd6a15e : kind === 'thrall' ? 0x7a6ea0 : 0x6de4e0, kind === 'boss' ? 0.07 : 0.08)));
  aura.position.y = 1.05;
  if (kind === 'boss') aura.visible = false;
  character.add(aura);
  const auraRing = kind === 'boss' ? markFineDetail(new THREE.Mesh(new THREE.TorusGeometry(1.55, 0.035, 6, 24), basic(0xf2c36e, 0.34))) : null;
  if (auraRing) {
    auraRing.rotation.x = Math.PI / 2;
    auraRing.position.y = 0.24;
    character.add(auraRing);
  }
  character.userData.parts = { body, head, leftArm, rightArm, leftLeg, rightLeg, cape, weapon, snuffer, snuffRing, aura, auraRing, silhouette, telegraph, packLink };

  if (kind === 'werewolf' || kind === 'boss') {
    const tail = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.9, 5), mat(palette.body));
    tail.position.set(0, 0.9, -0.48);
    tail.rotation.x = -1.15;
    character.add(tail);
    character.userData.parts.tail = tail;
  }

  attachShadow(character);
  character.traverse((object) => {
    if (object.isMesh) object.castShadow = !object.userData.noShadow;
  });
  character.userData.isCharacter = true;
  return character;
}

export function animateCharacter(character, dt, elapsed, options = {}) {
  if (!character?.userData?.parts) return;
  const data = character.userData;
  const parts = data.parts;
  const speed = options.speed || data.movement || 0;
  const gait = elapsed * (3.6 + speed * 0.7);
  const stride = Math.min(1, speed / 4.2);
  const reducedMotion = options.reducedMotion === true;
  data.impactPulse = Math.max(0, (data.impactPulse || 0) - dt * 5);
  const impactRatio = Math.min(1, data.impactPulse / 0.22);
  const impactWave = reducedMotion ? 0 : Math.sin(impactRatio * Math.PI);
  if (parts.leftLeg) parts.leftLeg.rotation.x = Math.sin(gait) * 0.64 * stride;
  if (parts.auraRing) {
    parts.auraRing.rotation.z = elapsed * 0.7;
    parts.auraRing.scale.setScalar(1 + Math.sin(elapsed * 2.1 + data.patrolSeed) * 0.08);
  }
  if (parts.packLink) {
    const linkMotion = reducedMotion ? 0 : 1;
    const linkPulse = 0.5 + 0.5 * Math.sin(elapsed * (data.linkPhase === 'telegraph' ? 10 : 3.4) + data.patrolSeed);
    parts.packLink.outer.rotation.z = elapsed * 0.9 * linkMotion;
    parts.packLink.inner.rotation.z = -elapsed * 1.35 * linkMotion;
    parts.packLink.beacon.rotation.y = elapsed * 1.8 * linkMotion;
    parts.packLink.outerMaterial.opacity = data.linkPhase === 'telegraph' ? 0.42 + linkPulse * 0.46 : data.linkPhase === 'recovery' ? 0.24 : 0.16;
    parts.packLink.innerMaterial.opacity = data.linkPhase === 'telegraph' ? 0.62 + linkPulse * 0.3 : data.linkPhase === 'recovery' ? 0.3 : 0.2;
    parts.packLink.group.scale.setScalar(1 + (data.linkPhase === 'telegraph' ? linkPulse * 0.12 : 0));
  }
  if (parts.snuffRing) {
    const channelling = data.snuffPhase === 'channel';
    parts.snuffRing.visible = channelling;
    if (channelling) {
      const spent = 1 - Math.min(1, Math.max(0, data.snuffTimer || 0) / SNUFFER.channelSeconds);
      const spin = reducedMotion ? 0 : elapsed * (1.1 + spent * 4.2);
      parts.snuffRing.rotation.z = spin;
      parts.snuffRing.scale.setScalar(1.4 - spent * 0.4);
      parts.snuffRing.material.opacity = 0.24 + spent * 0.52;
      parts.snuffRing.material.color.setHex(data.snuffGuarding ? 0xd6a15e : 0xc25a6a);
    }
  }
  if (parts.snuffer && data.snuffPhase === 'channel') {
    // The hook is raised over the bowl and dipped, which is the same tell a swing gives — the pose
    // says "this is finishing" before the ring has finished counting down.
    const dip = reducedMotion ? 0.5 : Math.abs(Math.sin(elapsed * 6.4)) * 0.5 + 0.5;
    parts.snuffer.group.rotation.x = -0.5 - dip * 0.5;
    parts.snuffer.ember.material.opacity = 0.5 + dip * 0.5;
  } else if (parts.snuffer) parts.snuffer.group.rotation.x = -0.1;
  if (parts.rightLeg) parts.rightLeg.rotation.x = -Math.sin(gait) * 0.64 * stride;
  if (parts.leftArm) parts.leftArm.rotation.x = -Math.sin(gait) * 0.42 * stride;
  if (parts.rightArm) parts.rightArm.rotation.x = Math.sin(gait) * 0.42 * stride;
  if (parts.body) parts.body.position.y = 0.92 + Math.abs(Math.sin(gait)) * 0.035 * stride;
  if (parts.head) parts.head.rotation.y = Math.sin(elapsed * 0.55 + data.patrolSeed) * 0.08;
  if (parts.aura) parts.aura.scale.setScalar((1 + Math.sin(elapsed * 2.4 + data.patrolSeed) * 0.06) * (1 + impactWave * 0.24));
  if (parts.aura?.material && data.kind !== 'boss') parts.aura.material.opacity = data.attackTimer > 0 || data.parryTimer > 0 ? 0.08 : 0.018;
  if (parts.cape) parts.cape.rotation.x = -0.1 + Math.sin(gait + 0.7) * 0.045 * stride;
  if (parts.tail) parts.tail.rotation.y = Math.sin(gait * 0.5) * 0.22;
  if (data.state === 'attack' || data.attackTimer > 0) {
    const phaseDuration = data.attackPhase === 'windup' ? data.attackWindupDuration
      : data.attackPhase === 'contact' ? data.attackContactDuration
        : data.attackRecoveryDuration;
    const remaining = Math.max(0, data.attackTimer || 0);
    const progress = phaseDuration > 0 ? Math.max(0, Math.min(1, 1 - remaining / phaseDuration)) : 1;
    const easeOut = 1 - (1 - progress) ** 3;
    const swing = data.attackPhase === 'windup' ? -0.35 - easeOut * 1.2
      : data.attackPhase === 'contact' ? -1.72
        : data.attackPhase === 'recovery' ? -0.32 + easeOut * 0.58
          : -0.2;
    if (parts.rightArm) parts.rightArm.rotation.x = -1.35 * swing;
    if (parts.body) parts.body.rotation.y = data.attackPhase === 'windup' ? -0.2 * easeOut : data.attackPhase === 'contact' ? 0.24 : 0.24 * (1 - easeOut);
    if (parts.body) parts.body.rotation.x = data.attackPhase === 'windup' ? -0.06 * easeOut : data.attackPhase === 'contact' ? 0.12 : 0;
    if (parts.head) parts.head.rotation.x = data.attackPhase === 'windup' ? -0.08 * easeOut : data.attackPhase === 'contact' ? 0.1 : 0;
    if (parts.weapon) {
      parts.weapon.rotation.z = -0.3 - swing * 1.8;
      parts.weapon.rotation.x = swing * 0.6;
    }
    if (parts.aura?.material) parts.aura.material.color.setHex(data.attackPhase === 'windup' ? 0xe8bb69 : data.attackPhase === 'contact' ? 0xef6b72 : 0x6de4e0);
    const telegraph = parts.telegraph;
    if (telegraph) {
      const phase = data.attackPhase;
      const pulse = 0.5 + 0.5 * Math.sin(elapsed * 13 + data.patrolSeed);
      const cue = parts.telegraph.userData;
      telegraph.visible = true;
      if (cue.material) {
        cue.material.color.setHex(phase === 'windup' ? 0xe8bb69 : phase === 'contact' ? 0xef6b72 : 0x6de4e0);
        cue.material.opacity = phase === 'windup' ? 0.2 + easeOut * 0.48 : phase === 'contact' ? 0.58 + pulse * 0.3 : 0.12 + (1 - progress) * 0.2;
      }
      if (cue.ground) {
        const scale = phase === 'windup' ? 0.7 + easeOut * 0.8 : phase === 'contact' ? 1.25 + pulse * 0.18 : 1.35;
        cue.ground.scale.setScalar(scale);
        cue.ground.rotation.z = elapsed * 1.8;
      }
      if (cue.blade) {
        const bladeScale = phase === 'windup' ? 0.72 + easeOut * 0.45 : phase === 'contact' ? 1.1 + pulse * 0.16 : 0.92;
        cue.blade.scale.setScalar(bladeScale);
        cue.blade.rotation.z = -0.7 - easeOut * 1.2;
      }
    }
  } else if (data.state === 'dodge') {
    if (parts.body) { parts.body.rotation.x = reducedMotion ? -0.16 : -0.46; parts.body.rotation.y = 0; }
    if (parts.head) parts.head.rotation.x = reducedMotion ? 0 : 0.18;
    if (parts.leftArm) parts.leftArm.rotation.x = 0.65;
    if (parts.rightArm) parts.rightArm.rotation.x = 0.8;
    if (parts.weapon) { parts.weapon.rotation.x = 0.35; parts.weapon.rotation.z = -0.8; }
    if (parts.telegraph) parts.telegraph.visible = false;
  } else if (data.state === 'parry') {
    if (parts.body) { parts.body.rotation.x = 0; parts.body.rotation.y = -0.16; }
    if (parts.head) parts.head.rotation.x = 0.04;
    if (parts.leftArm) parts.leftArm.rotation.x = -1.05;
    if (parts.rightArm) parts.rightArm.rotation.x = -0.72;
    if (parts.weapon) { parts.weapon.rotation.x = 0.18; parts.weapon.rotation.z = 0.82; }
    // The window is 0.22 s and nothing else in the pose said how much of it was left, so pressing
    // the parry read as a button that did nothing. The stance drains to nothing as the window
    // closes: the last frame in which a blade can still be caught is the last frame you can see.
    const parryWindow = Number.isFinite(options.parryWindow) && options.parryWindow > 0 ? options.parryWindow : 0.22;
    const left = Math.max(0, Math.min(1, (data.parryTimer || 0) / parryWindow));
    if (parts.aura) {
      parts.aura.scale.setScalar(0.5 + left * 0.85);
      parts.aura.material.color.setHex(left > 0.34 ? 0xe8bb69 : 0xfff3d0);
    }
    if (parts.auraRing) parts.auraRing.scale.setScalar((0.4 + left * 1.1) * (1 + Math.sin(elapsed * 18) * 0.04 * left));
    if (parts.telegraph) parts.telegraph.visible = false;
  } else {
    if (parts.rightArm) parts.rightArm.rotation.x = Math.sin(gait) * 0.42 * stride;
    if (parts.body) { parts.body.rotation.x = 0; parts.body.rotation.y = 0; }
    if (parts.head) parts.head.rotation.x = 0;
    if (parts.weapon) {
      parts.weapon.rotation.z = -0.3;
      parts.weapon.rotation.x = 0;
    }
    if (parts.telegraph) parts.telegraph.visible = false;
  }
  if (data.state === 'dead') {
    character.rotation.z = THREE.MathUtils.lerp(character.rotation.z, Math.PI / 2, Math.min(1, dt * 5));
    if (parts.aura?.material) parts.aura.material.opacity = Math.max(0, parts.aura.material.opacity - dt * 0.2);
    if (parts.telegraph) parts.telegraph.visible = false;
  }
  data.hitFlash = Math.max(0, (data.hitFlash || 0) - dt * 4.5);
  const flash = reducedMotion ? data.hitFlash * 0.12 : Math.min(0.62, data.hitFlash * 0.9);
  parts.body?.traverse((object) => {
    if (object.isMesh && object.material?.emissive) object.material.emissive.setScalar(flash);
  });
  if (parts.body) parts.body.scale.set(1 + impactWave * 0.12, 1 - impactWave * 0.1, 1 + impactWave * 0.12);
  if (parts.silhouette?.crest) parts.silhouette.crest.rotation.y = elapsed * 1.1;
  if (parts.silhouette?.mantle) parts.silhouette.mantle.rotation.y = Math.sin(elapsed * 0.8 + data.patrolSeed) * 0.035;
}

export function pulseCharacter(character, color = 0xffffff, amount = 0.35, reducedMotion = false) {
  const parts = character?.userData?.parts;
  if (!parts) return;
  character.userData.hitFlash = reducedMotion ? amount * 0.3 : amount;
  character.userData.impactPulse = Math.max(character.userData.impactPulse || 0, reducedMotion ? 0 : amount);
  if (!parts.aura) return;
  parts.aura.material.color.setHex(color);
}

export function setCharacterState(character, state) {
  if (character?.userData) character.userData.state = state;
}

export function makeMara(position) {
  const mara = createCharacter('mara', { position, hp: 1, speed: 4.3, seed: 4 });
  mara.userData.name = 'Mara Quill';
  mara.userData.role = 'companion';
  return mara;
}
